import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/apiClient';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';
import useAuth from '../hooks/useAuth';
import SortFilter, { sortItems } from '../components/SortFilter';

const emptyAnnouncement = { branch_id: '', title: '', body: '', type: 'general', is_pinned: false };

export default function AnnouncementsPage() {
  const navigate = useNavigate();
  const { user, currentBranch } = useAuth();

  const [announcements, setAnnouncements] = useState([]);
  const [branches, setBranches] = useState([]);
  const [sortOption, setSortOption] = useState('newest');
  const [form, setForm] = useState(emptyAnnouncement);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);

  // Edit State
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', body: '', type: 'general', is_pinned: false });
  const [editFile, setEditFile] = useState(null);

  useEffect(() => {
    load();
  }, [currentBranch]);

  async function load() {
    try {
      const params = {};
      if (currentBranch) {
        params.branch_id = currentBranch.branch_id;
      }
      const [announcementResponse, branchResponse] = await Promise.all([
        api.get('/announcements', { params }),
        api.get('/admin/branches'),
      ]);
      const data = announcementResponse.data.data || [];
      setAnnouncements(data);
      setBranches(branchResponse.data.data || []);
      if (selectedAnnouncement) {
        const updatedSelected = data.find(a => a.id === selectedAnnouncement.id);
        if (updatedSelected) setSelectedAnnouncement(updatedSelected);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load announcements');
    }
  }

  const sortedAnnouncements = sortItems(announcements, sortOption, 'title', 'created_at');

  const createAnnouncement = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');
    try {
      await api.post('/announcements', { ...form, branch_id: currentBranch?.branch_id });
      setForm(emptyAnnouncement);
      setIsAddModalOpen(false);
      setSuccessMessage(
        user?.role === 'teacher'
          ? 'Announcement submitted successfully and is pending approval.'
          : 'Announcement created successfully.'
      );
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to create announcement');
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    setError('');
    setSuccessMessage('');
    try {
      await api.put(`/announcements/${id}/status`, { status: newStatus });
      setSuccessMessage(`Announcement status updated to ${newStatus}`);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update status');
    }
  };

  const startEdit = (item) => {
    setEditForm({
      title: item.title || '',
      body: item.body || '',
      type: item.type || 'general',
      is_pinned: item.is_pinned === 1 || item.is_pinned === true
    });
    setEditFile(null);
    setIsEditing(true);
  };

  const saveAnnouncementEdit = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setSuccessMessage('');
    try {
      const formData = new FormData();
      formData.append('title', editForm.title);
      formData.append('body', editForm.body);
      formData.append('type', editForm.type);
      formData.append('is_pinned', editForm.is_pinned ? 1 : 0);
      if (editFile) {
        formData.append('attachment', editFile);
      }
      const res = await api.put(`/admin/announcements/${selectedAnnouncement.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSuccessMessage('Announcement updated successfully.');
      setIsEditing(false);
      if (res.data.data) {
        setSelectedAnnouncement(res.data.data);
      }
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update announcement');
    }
  };

  const isImageFile = (url) => {
    if (!url) return false;
    return /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(url) || url.includes('/uploads/announcement/');
  };

  return (
    <div>
      <PageHeader
        title="Announcement Management"
        subtitle="Create branch announcements and review history"
        action={
          <button type="button" className="primary-button" onClick={() => setIsAddModalOpen(true)} style={{ whiteSpace: 'nowrap' }}>
            + Add Announcement
          </button>
        }
      />
      {successMessage && <div className="success-message">{successMessage}</div>}
      {error && <div className="error-message">{error}</div>}

      <div className="page-header-row" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Announcements</h3>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
          <SortFilter sortOption={sortOption} onSortChange={setSortOption} />
        </div>
      </div>

      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add Announcement">
        <form className="inline-form" onSubmit={createAnnouncement}>
          <div style={{
            padding: '10px 14px',
            background: '#f8fafc',
            border: '1px solid #cbd5e1',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            color: '#334155',
            flex: 1
          }}>
            📍 Branch: {currentBranch?.name || 'Current Branch'}
          </div>
          <input placeholder="Title" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} required />
          <select value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}>
            <option value="general">general</option>
            <option value="event">event</option>
            <option value="urgent">urgent</option>
            <option value="reminder">reminder</option>
          </select>
          <textarea placeholder="Message" value={form.body} onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))} required />
          <div className="modal-actions">
            <button type="button" className="secondary-button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button type="submit" className="primary-button">Publish</button>
          </div>
        </form>
      </Modal>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Title</th>
              <th>Branch</th>
              <th>Posted By</th>
              <th>Type</th>
              <th>Status</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedAnnouncements.map((item) => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{item.title}</td>
                <td>{item.branch_name || '-'}</td>
                <td
                  onClick={() => { if (item.teacher_id) navigate(`/teachers/${item.teacher_id}?from=announcements`); }}
                  style={{
                    cursor: item.teacher_id ? 'pointer' : 'default',
                    textDecoration: item.teacher_id ? 'underline' : 'none',
                    textDecorationColor: '#6b7280'
                  }}
                >
                  {item.author_name || item.created_by || '-'}
                </td>
                <td>{item.type || '-'}</td>
                <td>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    background: item.status === 'approved' ? '#d1fae5' : (item.status === 'rejected' ? '#fee2e2' : '#fef3c7'),
                    color: item.status === 'approved' ? '#065f46' : (item.status === 'rejected' ? '#991b1b' : '#92400e')
                  }}>
                    {item.status || 'pending'}
                  </span>
                </td>
                <td>{item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}</td>
                <td>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      type="button"
                      style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        background: '#2563eb',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 600
                      }}
                      onClick={() => {
                        setSelectedAnnouncement(item);
                        setIsEditing(false);
                      }}
                    >
                      Detail
                    </button>

                    {(user?.role === 'admin' || user?.role === 'center_manager') && item.status === 'pending' && (
                      <>
                        <button
                          type="button"
                          style={{
                            padding: '4px 10px',
                            fontSize: '12px',
                            background: '#10b981',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: 600
                          }}
                          onClick={() => handleStatusUpdate(item.id, 'approved')}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          style={{
                            padding: '4px 10px',
                            fontSize: '12px',
                            background: '#ef4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: 600
                          }}
                          onClick={() => handleStatusUpdate(item.id, 'rejected')}
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Announcement Detail / Edit Modal */}
      {selectedAnnouncement && (
        <Modal
          isOpen={!!selectedAnnouncement}
          onClose={() => {
            setSelectedAnnouncement(null);
            setIsEditing(false);
          }}
          title={isEditing ? `Edit Announcement #${selectedAnnouncement.id}` : `Announcement Details #${selectedAnnouncement.id}`}
        >
          {isEditing ? (
            <form onSubmit={saveAnnouncementEdit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Title</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm(p => ({ ...p, title: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Type</label>
                  <select
                    value={editForm.type}
                    onChange={(e) => setEditForm(p => ({ ...p, type: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                  >
                    <option value="general">general</option>
                    <option value="event">event</option>
                    <option value="urgent">urgent</option>
                    <option value="reminder">reminder</option>
                  </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 22 }}>
                  <input
                    type="checkbox"
                    id="is_pinned_chk"
                    checked={editForm.is_pinned}
                    onChange={(e) => setEditForm(p => ({ ...p, is_pinned: e.target.checked }))}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                  <label htmlFor="is_pinned_chk" style={{ fontSize: 14, fontWeight: 600, color: '#334155', cursor: 'pointer' }}>
                    📌 Pin Announcement
                  </label>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Message Content</label>
                <textarea
                  rows={6}
                  value={editForm.body}
                  onChange={(e) => setEditForm(p => ({ ...p, body: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, lineHeight: 1.5 }}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                  Attachment Image/File (Optional Upload New)
                </label>
                {selectedAnnouncement.attachment && !editFile && (
                  <div style={{ marginBottom: 8, fontSize: 13, color: '#64748b' }}>
                    Current Attachment: {selectedAnnouncement.attachment}
                  </div>
                )}
                <input
                  type="file"
                  onChange={(e) => setEditFile(e.target.files[0] || null)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
              </div>

              <div className="modal-actions" style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16, marginTop: 8 }}>
                <button type="button" className="secondary-button" onClick={() => setIsEditing(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary-button">
                  Save Changes
                </button>
              </div>
            </form>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Header Box */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '14px 18px',
                background: '#f8fafc',
                borderRadius: '10px',
                border: '1px solid #e2e8f0'
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                      {selectedAnnouncement.title}
                    </h3>
                    {selectedAnnouncement.is_pinned === 1 && (
                      <span style={{
                        padding: '2px 8px',
                        background: '#fef3c7',
                        color: '#92400e',
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        📌 Pinned
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 13, color: '#64748b' }}>
                    Type: <strong style={{ textTransform: 'capitalize' }}>{selectedAnnouncement.type || 'general'}</strong>
                  </span>
                </div>
                <span style={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  background: selectedAnnouncement.status === 'approved' ? '#d1fae5' : selectedAnnouncement.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                  color: selectedAnnouncement.status === 'approved' ? '#065f46' : selectedAnnouncement.status === 'rejected' ? '#991b1b' : '#92400e'
                }}>
                  {selectedAnnouncement.status || 'pending'}
                </span>
              </div>

              {/* Info Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 12,
                padding: '12px 16px',
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '10px'
              }}>
                <div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Posted By</div>
                  <div style={{ fontSize: 14, color: '#1e293b', fontWeight: 600 }}>{selectedAnnouncement.author_name || selectedAnnouncement.created_by || '-'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Branch</div>
                  <div style={{ fontSize: 14, color: '#1e293b', fontWeight: 600 }}>{selectedAnnouncement.branch_name || '-'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Date Posted</div>
                  <div style={{ fontSize: 14, color: '#1e293b', fontWeight: 600 }}>
                    {selectedAnnouncement.created_at ? new Date(selectedAnnouncement.created_at).toLocaleString() : '-'}
                  </div>
                </div>
              </div>

              {/* Body / Message Content */}
              <div>
                <h4 style={{ margin: '0 0 6px', fontSize: 14, color: '#334155' }}>Announcement Message</h4>
                <div style={{
                  padding: '14px 16px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: 14,
                  color: '#334155',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.6
                }}>
                  {selectedAnnouncement.body || <span style={{ color: '#94a3b8' }}>No content provided.</span>}
                </div>
              </div>

              {/* Attachment Section */}
              <div>
                <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#334155' }}>Attachment</h4>
                {selectedAnnouncement.attachment ? (
                  isImageFile(selectedAnnouncement.attachment) ? (
                    <div
                      onClick={() => setPreviewPhoto(selectedAnnouncement.attachment)}
                      style={{
                        width: '100%',
                        maxHeight: '260px',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: '1px solid #cbd5e1',
                        background: '#000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <img
                        src={selectedAnnouncement.attachment}
                        alt="Announcement attachment"
                        style={{ maxWidth: '100%', maxHeight: '260px', objectFit: 'contain' }}
                      />
                    </div>
                  ) : (
                    <a
                      href={selectedAnnouncement.attachment}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '10px 16px',
                        background: '#eff6ff',
                        color: '#2563eb',
                        border: '1px solid #bfdbfe',
                        borderRadius: '8px',
                        fontSize: 14,
                        fontWeight: 600,
                        textDecoration: 'none'
                      }}
                    >
                      📎 Download Attachment File
                    </a>
                  )
                ) : (
                  <div style={{
                    padding: '14px',
                    background: '#f8fafc',
                    border: '1px dashed #cbd5e1',
                    borderRadius: '8px',
                    textAlign: 'center',
                    color: '#94a3b8',
                    fontSize: 13
                  }}>
                    📎 No file attached to this announcement.
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="modal-actions" style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16, marginTop: 8 }}>
                <button
                  type="button"
                  className="primary-button"
                  style={{ background: '#2563eb', borderColor: '#2563eb' }}
                  onClick={() => startEdit(selectedAnnouncement)}
                >
                  ✏️ Edit Announcement
                </button>

                {(user?.role === 'admin' || user?.role === 'center_manager') && selectedAnnouncement.status === 'pending' && (
                  <>
                    <button
                      type="button"
                      className="primary-button"
                      style={{ background: '#10b981', borderColor: '#10b981' }}
                      onClick={() => handleStatusUpdate(selectedAnnouncement.id, 'approved')}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="primary-button"
                      style={{ background: '#ef4444', borderColor: '#ef4444' }}
                      onClick={() => handleStatusUpdate(selectedAnnouncement.id, 'rejected')}
                    >
                      Reject
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setSelectedAnnouncement(null)}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* Lightbox Photo Preview */}
      {previewPhoto && (
        <div
          onClick={() => setPreviewPhoto(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24
          }}
        >
          <img
            src={previewPhoto}
            alt="Preview"
            style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: '8px', objectFit: 'contain' }}
          />
          <button
            onClick={() => setPreviewPhoto(null)}
            style={{
              position: 'absolute',
              top: 20,
              right: 20,
              background: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: 36,
              height: 36,
              fontSize: 20,
              cursor: 'pointer',
              fontWeight: 700
            }}
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
}

